import type {PairedToolCall, TurnItem, ToolCallItem} from '../../types/events'
import type {ToolName, ToolArgs, Delta} from '../../types/session'
import {getToolGroupingKey} from '../../constants/tool-names'

const ERROR_OPEN_TAG = ['<', 'error', '>'].join('')
const ERROR_CLOSE_TAG = ['</', 'error', '>'].join('')

/**
 * Maps a PairedToolCall from the events model to the props expected by ToolRenderer.
 *
 * Key insights:
 * - ToolRenderer only reads delta.content (not other Delta fields)
 * - arguments is already a parsed object from the event (no JSON.parse needed)
 * - undefined content signals "pending" (ToolRenderer shows shimmer)
 * - error tags in content signal failure (parseContent strips them)
 */
export function pairedToolCallToToolProps(toolCall: PairedToolCall): {
  name: ToolName
  args: ToolArgs
  delta: Delta
} {
  const {completeEvent} = toolCall

  let content: string | undefined
  if (completeEvent) {
    if (completeEvent.data.success) {
      const result = completeEvent.data.result
      content = result?.detailedContent ?? result?.content ?? ''
    } else {
      const errorMsg = completeEvent.data.error?.message ?? 'Tool execution failed'
      content = `${ERROR_OPEN_TAG}${errorMsg}${ERROR_CLOSE_TAG}`
    }
  }

  return {
    name: toolCall.toolName,
    args: toolCall.arguments ?? {},
    delta: {content},
  }
}

/**
 * A grouped or standalone item from a turn's item list.
 *
 * `single` — rendered as-is (non-tool items, shell tools, or lone tool calls).
 * `tool_group` — 2+ consecutive tool calls collapsed under a group header.
 */
export type ItemGroup =
  | {type: 'single'; item: TurnItem}
  | {type: 'tool_group'; id: string; toolName: string; items: ToolCallItem[]; isSetupGroup: boolean}

/**
 * Groups consecutive same-name tool calls within a turn's items.
 *
 * Operates on typed `TurnItem[]` — no JSON parsing needed since tool names
 * are already extracted.
 *
 * Rules:
 * - Shell tools (bash, powershell variants) are always standalone.
 * - Task tools are always standalone (subagents are a separate item kind).
 * - Setup tools (run_setup, run_custom_setup_step) group together regardless of
 *   specific tool name.
 * - All other tools group with consecutive calls of the same name.
 * - Non-tool items (assistant_text, prompts, etc.) break any active grouping.
 * - Single-item groups are flattened back to `single`.
 */
export function groupTurnItems(items: TurnItem[]): ItemGroup[] {
  const result: ItemGroup[] = []

  for (const item of items) {
    if (item.kind !== 'tool_call') {
      result.push({type: 'single', item})
      continue
    }

    const toolName = item.toolCall.toolName
    const groupingKey = getToolGroupingKey(toolName)
    if (groupingKey === null) {
      result.push({type: 'single', item})
      continue
    }

    const isSetup = groupingKey === 'setup'
    const last = result[result.length - 1]

    // Try to continue existing group by matching grouping keys
    if (last?.type === 'tool_group') {
      const lastKey = last.isSetupGroup ? 'setup' : last.toolName
      if (groupingKey === lastKey) {
        last.items.push(item)
        continue
      }
    }

    // Start new potential group
    result.push({
      type: 'tool_group',
      id: `${toolName}-${item.id}`,
      toolName,
      items: [item],
      isSetupGroup: isSetup,
    })
  }

  // Flatten single-item tool groups back to singles
  return result.map(g => {
    if (g.type === 'tool_group' && g.items.length === 1) {
      const item = g.items[0]
      if (item) return {type: 'single' as const, item}
    }

    return g
  })
}
